import { msg } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import classNames from 'classnames';
import { Form, Formik, FormikHelpers } from 'formik';
import { Send } from 'lucide-react';
import { getIntegrationErrorMessage } from 'modules/integrations/domain/constants';
import { IntegrationStatus } from 'modules/integrations/domain/enums';
import { TelegramBotInfo, TelegramIntegration } from 'modules/integrations/domain/interfaces';
import { isTelegramInviteLink, normalizeTelegramChannelInput } from 'modules/integrations/domain/utils';
import {
  useCreateTelegramIntegrationMutation,
  useDeleteTournamentIntegrationMutation,
} from 'modules/integrations/store/tournament-integrations-api';
import React, { useState } from 'react';
import { toast } from 'react-toastify';
import UDFormInput from 'ud-form/components/input';
import UDButton from 'ud-ui/components/button';
import Modal from 'ud-ui/components/modal';
import UDText from 'ud-ui/components/text';
import * as yup from 'yup';

interface TelegramIntegrationCardProps {
  /** Id организации — backend `tournamentId`. */
  organizerId: number;
  /** `null` — канал не подключён. */
  integration: TelegramIntegration | null;
  /** `undefined`, пока не загрузилась информация о боте. */
  botInfo: TelegramBotInfo | undefined;
}

interface ConnectFormValues {
  channel: string;
}

const CONNECT_FALLBACK = msg`Не удалось подключить канал. Попробуйте ещё раз.`;
const DELETE_FALLBACK = msg`Не удалось удалить подключение. Попробуйте ещё раз.`;

type StatusKind = 'none' | 'active' | 'disconnected';

const STATUS_CLASSES: Record<StatusKind, string> = {
  none: 'border-lineStrong text-inkSecondary',
  active: 'border-CaseSuccess text-CaseSuccessText bg-CaseSuccessSurface',
  disconnected: 'border-CaseError text-CaseErrorText bg-CaseErrorSurface',
};

function statusKindOf(integration: TelegramIntegration | null): StatusKind {
  if (!integration) return 'none';
  return integration.status === IntegrationStatus.ACTIVE ? 'active' : 'disconnected';
}

/** Карточка Telegram во вкладке «Интеграции»: подключение канала к боту ImGame или подключённый канал. */
const TelegramIntegrationCard = ({ organizerId, integration, botInfo }: TelegramIntegrationCardProps) => {
  const { t } = useLingui();
  const [createIntegration] = useCreateTelegramIntegrationMutation();
  const [deleteIntegration, { isLoading: isDeleting }] = useDeleteTournamentIntegrationMutation();
  const [isDeleteConfirmShown, setIsDeleteConfirmShown] = useState(false);
  const statusKind = statusKindOf(integration);

  const validationSchema = yup.object({
    channel: yup
      .string()
      .trim()
      .required(t`Укажите адрес канала`)
      .test(
        'not-invite-link',
        t`Это ссылка-приглашение — по ней бот канал не найдёт. Для закрытого канала вставьте адрес из web.telegram.org (см. выше).`,
        (value) => !value || !isTelegramInviteLink(value),
      ),
  });

  const handleConnect = async ({ channel }: ConnectFormValues, helpers: FormikHelpers<ConnectFormValues>) => {
    try {
      await createIntegration({ organizerId, channel: normalizeTelegramChannelInput(channel) }).unwrap();
      toast.success(t`Канал подключён`);
    } catch (error) {
      helpers.setFieldError('channel', t(getIntegrationErrorMessage(error, CONNECT_FALLBACK)));
    }
  };

  const handleDelete = async () => {
    if (!integration) return;
    try {
      await deleteIntegration({ integrationId: integration.id, organizerId }).unwrap();
      setIsDeleteConfirmShown(false);
      toast.success(t`Канал отключён`);
    } catch (error) {
      toast.error(t(getIntegrationErrorMessage(error, DELETE_FALLBACK)));
    }
  };

  const openBotLink = () => {
    if (botInfo) window.open(botInfo.addToChannelUrl, '_blank', 'noopener,noreferrer');
  };

  const channelTitle = (integration?.channelUsername ? `@${integration.channelUsername}` : integration?.chatId) ?? '';
  const botName = botInfo ? `@${botInfo.username}` : '…';

  return (
    <section
      className={classNames(
        'bg-surfaceCard rounded-card flex flex-col gap-4 border p-4',
        statusKind === 'active' ? 'border-CaseSuccess' : 'border-lineSubtle',
      )}
      data-testid="telegram-integration-card"
    >
      <header className="flex items-center gap-3">
        <span className="bg-surfaceSunken text-inkSecondary flex h-10 w-10 shrink-0 items-center justify-center rounded-full">
          <Send size={18} aria-hidden />
        </span>
        <div className="flex flex-col items-start gap-1">
          <UDText type="subhead" className="text-inkPrimary font-semibold">
            Telegram
          </UDText>
          <span
            className={classNames('rounded-control border px-2 py-0.5 text-xs', STATUS_CLASSES[statusKind])}
            data-testid="telegram-integration-status"
          >
            {statusKind === 'none' && <Trans>Не подключено</Trans>}
            {statusKind === 'active' && <Trans>Подключено</Trans>}
            {statusKind === 'disconnected' && <Trans>Нет доступа</Trans>}
          </span>
        </div>
      </header>

      {integration ? (
        <div className="border-lineSubtle flex flex-col gap-3 border-t pt-4">
          {statusKind === 'disconnected' && (
            <UDText type="footnote" className="text-CaseErrorText block" data-testid="telegram-integration-disconnected">
              <Trans>
                Бот потерял доступ к каналу — посты не публикуются. Верните боту права администратора с публикацией сообщений или
                удалите подключение и подключите канал заново.
              </Trans>
            </UDText>
          )}
          <div className="flex flex-col gap-1">
            <UDText type="footnote" className="text-inkSecondary">
              <Trans>Канал Telegram</Trans>
            </UDText>
            {integration.channelUrl ? (
              <a
                href={integration.channelUrl}
                target="_blank"
                rel="noreferrer"
                className="text-inkPrimary font-semibold underline"
                data-testid="telegram-integration-channel"
              >
                {channelTitle}
              </a>
            ) : (
              <UDText type="subhead" className="text-inkPrimary font-semibold" data-testid="telegram-integration-channel">
                {channelTitle}
              </UDText>
            )}
          </div>
          <div>
            <UDButton
              variant="secondary"
              type="button"
              onClick={() => setIsDeleteConfirmShown(true)}
              data-testid="telegram-integration-delete"
            >
              <Trans>Удалить</Trans>
            </UDButton>
          </div>
        </div>
      ) : (
        <div className="border-lineSubtle flex flex-col gap-4 border-t pt-4">
          <div className="flex flex-col gap-2">
            <UDText type="subhead" className="text-inkPrimary font-semibold">
              <Trans>Как подключить</Trans>
            </UDText>
            {/* Номера — разметкой, а не `list-decimal`: глобальный сброс стилей прячет маркеры списков. */}
            <ol className="text-inkSecondary flex flex-col gap-3 text-sm">
              <li className="flex gap-2">
                <span>1.</span>
                <span className="flex flex-col items-start gap-2">
                  <span>
                    <Trans>Добавьте бота {botName} в администраторы канала с правом публикации сообщений.</Trans>
                  </span>
                  {/* Кнопка — внутри шага, к которому относится: под списком её путали со вторым шагом. */}
                  <UDButton variant="primary" type="button" onClick={openBotLink} disabled={!botInfo} data-testid="telegram-add-bot">
                    <Trans>Добавить бота в канал</Trans>
                  </UDButton>
                </span>
              </li>
              <li className="flex gap-2">
                <span>2.</span>
                <span className="flex flex-col gap-2">
                  <span>
                    <Trans>Вставьте адрес канала в поле ниже и нажмите «Сохранить». Где взять адрес:</Trans>
                  </span>
                  <span data-testid="telegram-help-public">
                    <Trans>
                      <b className="text-inkPrimary">Публичный канал</b> — откройте канал в Telegram и нажмите на его название вверху.
                      В описании будет ссылка вида t.me/имя_канала — скопируйте её.
                    </Trans>
                  </span>
                  <span data-testid="telegram-help-private">
                    <Trans>
                      <b className="text-inkPrimary">Закрытый канал</b> (ссылка в описании начинается с t.me/+) — такая ссылка не
                      подойдёт. Откройте канал в браузере на{' '}
                      <a href="https://web.telegram.org/a/" target="_blank" rel="noreferrer" className="text-inkPrimary underline">
                        web.telegram.org
                      </a>{' '}
                      и скопируйте адрес из адресной строки целиком — он выглядит как web.telegram.org/a/#-1001234567890.
                    </Trans>
                  </span>
                </span>
              </li>
            </ol>
          </div>
          <Formik<ConnectFormValues> initialValues={{ channel: '' }} validationSchema={validationSchema} onSubmit={handleConnect}>
            {({ isSubmitting }) => (
              <Form className="flex flex-col gap-3" noValidate>
                <UDFormInput
                  name="channel"
                  label={t`Канал Telegram`}
                  placeholder={t`t.me/channel, @channel или адрес из web.telegram.org`}
                  containerProps={{ className: 'basis-full' }}
                  data-testid="telegram-channel-input"
                />
                <div>
                  <UDButton variant="primary" type="submit" loading={isSubmitting} data-testid="telegram-integration-save">
                    <Trans>Сохранить</Trans>
                  </UDButton>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      )}

      <Modal.Confirm
        show={isDeleteConfirmShown}
        title={t`Отключить канал ${channelTitle}?`}
        description={t`Посты перестанут публиковаться в этот канал. Подключить его можно будет снова.`}
        confirmText={t`Удалить`}
        isLoading={isDeleting}
        onConfirm={() => void handleDelete()}
        onCancel={() => setIsDeleteConfirmShown(false)}
      />
    </section>
  );
};

export default React.memo(TelegramIntegrationCard);
